import { customAuth } from "../standardAuthPatterns";
import logo from "./logo.svg";
import { defineProduct, productVariant } from "../types";

const KEYWORDS =
  "repositories pull requests issues actions ci cd source control ghes enterprise server";

export const github = defineProduct({
  name: "GitHub",
  tagLine: "Code hosting and collaboration",
  logo,
  tags: ["featured", "engineering"],
  variants: [
    productVariant({
      identifier: "github",
      authLabel: "User OAuth",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://(?:api\\.)?github\\.com/.*",
      auth: customAuth("github"),
      steps: [
        {
          title: "Begin creating a GitHub OAuth app",
          items: [
            {
              kind: "markdown",
              content:
                "Visit your [developer settings](https://github.com/settings/developers) and click **'New OAuth App'**.",
            },
          ],
        },
        {
          title: "Fill out the details",
          items: [
            {
              kind: "markdown",
              content:
                "Copy and paste the following information into the form:",
            },
            {
              kind: "copyable",
              label: "Application name",
              value: "3B",
            },
            {
              kind: "copyable",
              label: "Homepage URL",
              value: "{UI_URL}",
            },
            {
              kind: "copyable",
              label: "Authorization callback URL",
              value: "{API_URL}/connected-apps/oauth/github/callback",
            },
          ],
        },
        {
          title: "Save",
          items: [
            {
              kind: "markdown",
              content:
                "Click **'Register application'** to submit the form and save.",
            },
          ],
        },
        {
          title: "Generate a client secret",
          items: [
            {
              kind: "markdown",
              content:
                "On your newly created OAuth app, click **Generate a new client secret**.",
            },
          ],
        },
        {
          title: "Input your client ID and generated secret",
          items: [
            {
              kind: "input",
              label: "Client ID",
              placeholder: "Paste your Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              placeholder: "Paste your Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
        {
          title: "If applicable, enter your GitHub organization",
          items: [
            {
              kind: "input",
              label: "GitHub organization (optional)",
              placeholder: "github.example.com",
              helperText:
                "The hostname of your GitHub Enterprise Server instance",
              target: { kind: "configuration", field: "organizationHost" },
            },
          ],
        },
        {
          title: "Define scopes for this connection",
          items: [
            {
              kind: "markdown",
              content:
                "Add [scopes](https://docs.github.com/en/apps/oauth-apps/building-oauth-apps/scopes-for-oauth-apps#available-scopes) in a space separated list below.",
            },
            {
              kind: "input",
              label: "Scopes (optional)",
              defaultValue: "repo workflow",
              helperText: "Space separated list of desired scopes.",
              target: { kind: "oauthConnection", field: "scopes" },
            },
          ],
        },
        {
          title: "Authorize your account in GitHub",
          items: [
            {
              kind: "markdown",
              content:
                "Finally, we’ll authorize your desired GitHub account for this GitHub app so it can be used in steps in 3B.",
            },
            {
              kind: "oauth",
              dependsOn: [
                { kind: "oauthConnection", field: "clientId" },
                { kind: "oauthConnection", field: "clientSecret" },
              ],
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () => "https://github.com/login/oauth/authorize",
        tokenEndpoint: () => "https://github.com/login/oauth/access_token",
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "db",
        scopes: () => ["repo", "workflow"],
        authorizationParameters: () => ({
          allow_signup: "false",
        }),
      },
      connectionTest: {
        protocol: "http",
        request: () => "https://api.github.com/user",
      },
    }),
    productVariant({
      identifier: "githubApp",
      authLabel: "GitHub App",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://(?:api\\.)?github\\.com/.*",
      auth: customAuth("githubApp"),
      steps: [
        {
          title: "Create and install the GitHub App",
          items: [
            {
              kind: "markdown",
              content:
                "We’ll open GitHub with a preconfigured app for you to review and create, then install on the repositories you choose. The app ID, private key, and installation ID below fill in automatically.",
            },
            {
              kind: "input",
              label: "GitHub organization (optional)",
              placeholder: "my-org",
              helperText:
                "Create the app under this organization instead of your personal account.",
              target: { kind: "configuration", field: "organizationLogin" },
            },
            {
              kind: "githubAppManifest",
            },
          ],
        },
        {
          title: "Or configure an existing app manually",
          items: [
            {
              kind: "markdown",
              content:
                "If you already have a GitHub App (or use GitHub Enterprise Server), enter its details here. Find the app ID under **‘About’** on the app’s settings page, generate a private key under **‘Private keys’**, and read the installation ID from the URL after installing (`https://github.com/settings/installations/12345678`).",
            },
            {
              kind: "input",
              label: "App ID",
              placeholder: "123456",
              required: true,
              target: { kind: "configuration", field: "appId" },
            },
            {
              kind: "file",
              label: "Private key (.pem)",
              accept: ".pem",
              helperText: "The .pem file downloaded from the app’s settings.",
              target: { kind: "secrets", field: "privateKey" },
            },
            {
              kind: "input",
              label: "Installation ID",
              placeholder: "12345678",
              required: true,
              target: { kind: "configuration", field: "installationId" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () =>
          "https://api.github.com/installation/repositories?per_page=1",
      },
      usageHint: () =>
        "This connection authenticates as a GitHub App installation, not as a user. Use the GitHub REST API at https://api.github.com.",
    }),
    productVariant({
      identifier: "githubToken",
      authLabel: "Personal access token",
      keywords: KEYWORDS,
      protocol: "http",
      defaultUrlPattern: () => "https://(?:api\\.)?github\\.com/.*",
      auth: customAuth("githubToken"),
      steps: [
        {
          title: "Create a personal access token",
          items: [
            {
              kind: "markdown",
              content:
                "Go to your [token settings](https://github.com/settings/tokens?type=beta) and click **'Generate new token'**. Select the repositories and permissions your workflows need, then click **'Generate token'**.",
            },
          ],
        },
        {
          title: "Enter your token",
          items: [
            {
              kind: "input",
              label: "Personal access token",
              placeholder: "github_pat_…",
              required: true,
              target: { kind: "secrets", field: "token" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () => "https://api.github.com/user",
      },
      usageHint: () =>
        "This connection authenticates with a personal access token. Use the GitHub REST API at https://api.github.com.",
    }),
  ],
});
